import { useState, useEffect, useMemo } from 'react';
import { Users, Search, Shield, Clock, Mail, Calendar, Globe, AlertTriangle, CheckCircle, XCircle, Trash2, Pause, RotateCcw } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/AuthContext';
import { useApp } from '../lib/AppContext';
import AdminShell from '../components/AdminShell';
import CivicModal from '../components/CivicModal';
import Toast from '../components/Toast';

interface UserProfile {
  id: string;
  email: string | null;
  display_name: string | null;
  is_admin: boolean;
  is_disabled: boolean;
  deleted_at: string | null;
  created_at: string;
  last_seen_at: string | null;
  country: string | null;
  country_code: string | null;
}

type ConfirmAction = 'deactivate' | 'reactivate' | 'delete' | 'reset_civic' | null;

export default function AdminUsers() {
  const { user } = useAuth();
  const { refreshUserData } = useApp();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadUsers();

    // Subscribe to profile changes to auto-refresh when names are updated
    const profileSubscription = supabase
      .channel('profile_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles'
      }, () => {
        loadUsers();
      })
      .subscribe();

    return () => {
      profileSubscription.unsubscribe();
    };
  }, []);

  async function loadUsers() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, display_name, is_admin, is_disabled, deleted_at, created_at, last_seen_at, country, country_code')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setToast({ message: 'Failed to load users', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        u.email?.toLowerCase().includes(searchLower) ||
        u.display_name?.toLowerCase().includes(searchLower);

      return matchesSearch;
    });
  }, [users, searchQuery]);

  const stats = useMemo(() => {
    return {
      total: users.length,
      active: users.filter(u => !u.is_disabled).length,
      inactive: users.filter(u => u.is_disabled).length,
      admins: users.filter(u => u.is_admin).length
    };
  }, [users]);

  function openUserDetail(user: UserProfile) {
    setSelectedUser(user);
    setEditIsAdmin(user.is_admin);
  }

  function closeUserDetail() {
    setSelectedUser(null);
    setConfirmAction(null);
  }

  async function saveUserChanges() {
    if (!selectedUser) return;

    try {
      setSaving(true);

      const { data, error } = await supabase.rpc('set_user_admin', {
        target_user_id: selectedUser.id,
        make_admin: editIsAdmin
      });

      if (error) throw error;

      setUsers(prev => prev.map(u =>
        u.id === selectedUser.id
          ? { ...u, is_admin: editIsAdmin }
          : u
      ));

      setToast({ message: 'User updated successfully', type: 'success' });
      closeUserDetail();
    } catch (err: any) {
      console.error('[AdminUsers] Error updating user:', err);
      setToast({
        message: err.message || 'Failed to update user',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivateUser() {
    if (!selectedUser) return;

    try {
      setActionLoading(true);

      const { data, error } = await supabase.rpc('deactivate_user', {
        target_user_id: selectedUser.id
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to deactivate user');
      }

      // Update selected user state immediately
      setSelectedUser(prev => prev ? { ...prev, is_disabled: true } : null);

      // Update users list
      setUsers(prev => prev.map(u =>
        u.id === selectedUser.id
          ? { ...u, is_disabled: true }
          : u
      ));

      setConfirmAction(null);
      setToast({ message: 'User deactivated', type: 'success' });
    } catch (err: any) {
      console.error('[AdminUsers] Error deactivating user:', err);
      setToast({
        message: err.message || 'Failed to deactivate user',
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReactivateUser() {
    if (!selectedUser) return;

    try {
      setActionLoading(true);

      const { data, error } = await supabase.rpc('reactivate_user', {
        target_user_id: selectedUser.id
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to reactivate user');
      }

      // Update selected user state immediately
      setSelectedUser(prev => prev ? { ...prev, is_disabled: false } : null);

      // Update users list
      setUsers(prev => prev.map(u =>
        u.id === selectedUser.id
          ? { ...u, is_disabled: false }
          : u
      ));

      setConfirmAction(null);
      setToast({ message: 'User reactivated', type: 'success' });
    } catch (err: any) {
      console.error('[AdminUsers] Error reactivating user:', err);
      setToast({
        message: err.message || 'Failed to reactivate user',
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteUser() {
    if (!selectedUser) return;

    try {
      setActionLoading(true);

      const { data, error } = await supabase.rpc('delete_user', {
        target_user_id: selectedUser.id
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete user');
      }

      // Remove from users list immediately
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));

      setToast({ message: 'User deleted', type: 'success' });
      closeUserDetail();
    } catch (err: any) {
      console.error('[AdminUsers] Error deleting user:', err);
      setToast({
        message: err.message || 'Failed to delete user',
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  }

  async function handleResetCivicProfile() {
    console.log('[AdminUsers] handleResetCivicProfile called');

    if (!selectedUser) {
      console.log('[AdminUsers] No selected user, returning');
      return;
    }

    try {
      setActionLoading(true);

      console.log('[AdminUsers] Resetting civic profile for user:', selectedUser.id);

      const { data, error } = await supabase.rpc('reset_civic_profile', {
        target_user_id: selectedUser.id
      });

      console.log('[AdminUsers] Reset response:', { data, error });

      if (error) {
        console.error('[AdminUsers] Supabase error:', error);
        throw error;
      }

      // Safely handle the response
      if (!data) {
        throw new Error('No response from reset function');
      }

      const result = data as { success: boolean; error?: string; message?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to reset civic profile');
      }

      console.log('[AdminUsers] Reset successful');

      // Close modal first
      setConfirmAction(null);

      // Show toast
      setToast({ message: 'Civic profile reset successfully', type: 'success' });

      console.log('[AdminUsers] Toast shown');

      // Refresh user data regardless of which user was reset
      const isResettingOwnProfile = selectedUser?.id === user?.id;

      if (isResettingOwnProfile) {
        console.log('[AdminUsers] Resetting own profile - refreshing data');
        await refreshUserData();
      }

      // Refresh the user list
      await loadUsers();
      console.log('[AdminUsers] User data refreshed');
    } catch (err: any) {
      console.error('[AdminUsers] Error resetting civic profile:', err);
      setToast({
        message: err.message || 'Failed to reset civic profile',
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  }

  function formatDate(dateString: string | null) {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  function formatDateTime(dateString: string | null) {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }

  const isCurrentUser = selectedUser?.id === user?.id;

  return (
    <AdminShell activeSection="users">
      <div className="p-4 sm:p-6 lg:p-8 w-full max-w-[1120px]">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#40E0D0]/10 flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#40E0D0]" />
            </div>
            <div>
              <h1 className="text-[24px] sm:text-[28px] font-semibold text-[#F5F5F5]">Users</h1>
              <p className="text-[13px] sm:text-[14px] text-[#A0A0A0]">Manage admin workspace access</p>
            </div>
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 sm:mb-8">
          <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
            <div className="text-[28px] font-bold text-[#F5F5F5] mb-1">
              {stats.total}
            </div>
            <div className="text-[13px] text-[#A0A0A0]">
              Total Users
            </div>
          </div>
          <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
            <div className="text-[28px] font-bold text-[#40E0D0] mb-1">
              {stats.active}
            </div>
            <div className="text-[13px] text-[#A0A0A0]">
              Active Users
            </div>
          </div>
          <div className="bg-[#181818] border border-[#282828] rounded-xl p-6">
            <div className="text-[28px] font-bold text-[#FFB86C] mb-1">
              {stats.admins}
            </div>
            <div className="text-[13px] text-[#A0A0A0]">
              Administrators
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#1A1A1A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] placeholder-[#808080] focus:outline-none focus:border-[#40E0D0]/50"
            />
          </div>
        </div>

        <div className="mb-4 text-[13px] text-[#A0A0A0]">
          {loading ? 'Loading...' : `${filteredUsers.length} user${filteredUsers.length !== 1 ? 's' : ''}`}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block bg-[#1A1A1A] border border-[#282828] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0A0A0A] border-b border-[#282828]">
                <tr>
                  <th className="px-6 py-4 text-left text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">Region</th>
                  <th className="px-6 py-4 text-left text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">Admin Access</th>
                  <th className="px-6 py-4 text-left text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">Created</th>
                  <th className="px-6 py-4 text-left text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider">Last Seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#282828]">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-[#808080]">Loading users...</td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-[#808080]">No users found</td>
                  </tr>
                ) : (
                  filteredUsers.map(u => (
                    <tr
                      key={u.id}
                      onClick={() => openUserDetail(u)}
                      className="hover:bg-[#1F1F1F] cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#40E0D0] to-[#52E3C2] flex items-center justify-center text-[#0A0A0A] font-semibold text-[14px]">
                            {u.display_name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <div className="text-[14px] font-medium text-[#F5F5F5]">{u.display_name || 'Unnamed User'}</div>
                            {u.id === user?.id && <div className="text-[12px] text-[#40E0D0] font-medium">You</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-[14px] text-[#A0A0A0]">
                          <Mail className="w-4 h-4 text-[#808080]" />
                          {u.email || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.is_disabled ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#FF6F61]/10 text-[#FF6F61]">
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#40E0D0]/10 text-[#40E0D0]">
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-[14px] text-[#A0A0A0]">
                          <Globe className="w-4 h-4 text-[#808080]" />
                          {u.country || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.is_admin ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#FFB86C]/10 text-[#FFB86C]">
                            <Shield className="w-3 h-3" />
                            Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#282828] text-[#A0A0A0]">
                            User
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-[14px] text-[#A0A0A0]">
                          <Calendar className="w-4 h-4 text-[#808080]" />
                          {formatDate(u.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-[14px] text-[#A0A0A0]">
                          <Clock className="w-4 h-4 text-[#808080]" />
                          {formatDateTime(u.last_seen_at)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-8 text-center text-[#808080]">
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-8 text-center text-[#808080]">
              No users found
            </div>
          ) : (
            filteredUsers.map(u => (
              <div
                key={u.id}
                onClick={() => openUserDetail(u)}
                className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4 active:bg-[#1F1F1F] transition-colors"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#40E0D0] to-[#52E3C2] flex items-center justify-center text-[#0A0A0A] font-semibold text-[16px] flex-shrink-0">
                    {u.display_name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-[15px] font-semibold text-[#F5F5F5] truncate">
                        {u.display_name || 'Unnamed User'}
                      </h3>
                      {u.id === user?.id && (
                        <span className="text-[11px] px-2 py-0.5 bg-[#40E0D0]/10 text-[#40E0D0] rounded-full font-medium flex-shrink-0">
                          You
                        </span>
                      )}
                      {u.is_disabled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FF6F61]/10 text-[#FF6F61] flex-shrink-0">
                          <XCircle className="w-3 h-3" />
                          Inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#40E0D0]/10 text-[#40E0D0] flex-shrink-0">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-[13px] text-[#A0A0A0] truncate">
                      {u.email || '—'}
                    </p>
                  </div>
                  {u.is_admin && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#FFB86C]/10 text-[#FFB86C] flex-shrink-0">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* User Detail Modal */}
        {selectedUser && !confirmAction && (
          <CivicModal
            isOpen={true}
            onClose={closeUserDetail}
            maxWidth="md"
            title={selectedUser.display_name || 'Unnamed User'}
            subtitle={selectedUser.email || undefined}
            icon={Users}
          >
            <div>
              {/* Account Overview */}
              <div className="mb-6">
                <h3 className="text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider mb-3">Account Overview</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2 text-[13px] text-[#C5C5C5]">
                      <CheckCircle className="w-4 h-4 text-[#808080]" />
                      Status
                    </div>
                    <div>
                      {selectedUser.deleted_at ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#505050]/20 text-[#909090]">
                          <XCircle className="w-3 h-3" />
                          Deleted
                        </span>
                      ) : selectedUser.is_disabled ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#FF6F61]/10 text-[#FF6F61]">
                          <XCircle className="w-3 h-3" />
                          Inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium bg-[#40E0D0]/10 text-[#40E0D0]">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2 text-[13px] text-[#C5C5C5]">
                      <Calendar className="w-4 h-4 text-[#808080]" />
                      Created
                    </div>
                    <div className="text-[14px] font-medium text-[#F5F5F5]">
                      {formatDate(selectedUser.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2 text-[13px] text-[#C5C5C5]">
                      <Clock className="w-4 h-4 text-[#808080]" />
                      Last Seen
                    </div>
                    <div className="text-[14px] font-medium text-[#F5F5F5]">
                      {formatDateTime(selectedUser.last_seen_at)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-2 text-[13px] text-[#C5C5C5]">
                      <Globe className="w-4 h-4 text-[#808080]" />
                      Region
                    </div>
                    <div className="text-[14px] font-medium text-[#F5F5F5]">
                      {selectedUser.country || '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div className="mb-6 pb-6 border-b border-[#282828]">
                <h3 className="text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider mb-3">Permissions</h3>
                <div>
                  <label className="block text-[13px] font-medium text-[#F5F5F5] mb-2">Admin Workspace Access</label>
                  <select
                    value={editIsAdmin ? 'admin' : 'user'}
                    onChange={(e) => setEditIsAdmin(e.target.value === 'admin')}
                    disabled={isCurrentUser || !!selectedUser.deleted_at}
                    className="w-full pl-3 pr-16 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50 disabled:opacity-50 disabled:cursor-not-allowed appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%23A0A0A0%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2010-1.414-1.414z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px_12px] bg-[right_12px_center] bg-no-repeat"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  {isCurrentUser ? (
                    <p className="mt-1.5 text-[12px] text-[#808080]">You cannot change your own role</p>
                  ) : (
                    <p className="mt-1.5 text-[12px] text-[#808080]">
                      Admin users can access the Admin Workspace and manage content
                    </p>
                  )}
                </div>
              </div>

              {/* Account Actions */}
              {!selectedUser.deleted_at && (
                <div className="mb-6">
                  <h3 className="text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider mb-3">Account Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setConfirmAction('reset_civic')}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-0 py-1 text-[#FFA500] hover:text-[#FFB84D] text-[13px] font-medium transition-colors disabled:opacity-50 cursor-pointer"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset civic profile
                    </button>
                    {isCurrentUser ? (
                      <div className="p-3 bg-[#40E0D0]/5 border border-[#40E0D0]/20 rounded-lg">
                        <p className="text-[13px] text-[#808080]">
                          You cannot modify your own account status.
                        </p>
                      </div>
                    ) : (
                      <>
                        {selectedUser.is_disabled ? (
                          <button
                            onClick={() => setConfirmAction('reactivate')}
                            disabled={actionLoading}
                            className="flex items-center gap-2 px-0 py-1 text-[#40E0D0] hover:text-[#47EAD8] text-[13px] font-medium transition-colors disabled:opacity-50 cursor-pointer"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Reactivate account
                          </button>
                        ) : (
                          <button
                            onClick={() => setConfirmAction('deactivate')}
                            disabled={actionLoading}
                            className="flex items-center gap-2 px-0 py-1 text-[#FF8C42] hover:text-[#FFA366] text-[13px] font-medium transition-colors disabled:opacity-50 cursor-pointer"
                          >
                            <Pause className="w-4 h-4" />
                            Deactivate account
                          </button>
                        )}
                        <button
                          onClick={() => setConfirmAction('delete')}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-0 py-1 text-[#FF6F61] hover:text-[#FF7D6F] text-[13px] font-medium transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete user
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {selectedUser.deleted_at && (
                <div className="mb-6">
                  <h3 className="text-[12px] font-semibold text-[#A0A0A0] uppercase tracking-wider mb-3">Account Actions</h3>
                  <div className="p-3 bg-[#505050]/10 border border-[#505050]/20 rounded-lg">
                    <p className="text-[13px] text-[#909090]">
                      This account has been deleted.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-[#282828]">
                <button
                  onClick={closeUserDetail}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveUserChanges}
                  disabled={
                    saving ||
                    isCurrentUser ||
                    !!selectedUser.deleted_at ||
                    editIsAdmin === selectedUser.is_admin
                  }
                  className="flex-1 px-4 py-2.5 bg-[#40E0D0] hover:brightness-105 text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </CivicModal>
        )}

        {/* Deactivate Confirmation Modal */}
        {selectedUser && confirmAction === 'deactivate' && (
          <CivicModal
            isOpen={true}
            onClose={() => setConfirmAction(null)}
            maxWidth="sm"
            title="Deactivate user?"
            icon={XCircle}
          >
            <div>
              <p className="text-[14px] text-[#C5C5C5] leading-relaxed mb-6">
                This user will no longer be able to sign in until reactivated.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivateUser}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#FF6F61] hover:brightness-105 text-white rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50"
                >
                  {actionLoading ? 'Deactivating...' : 'Deactivate'}
                </button>
              </div>
            </div>
          </CivicModal>
        )}

        {/* Reactivate Confirmation Modal */}
        {selectedUser && confirmAction === 'reactivate' && (
          <CivicModal
            isOpen={true}
            onClose={() => setConfirmAction(null)}
            maxWidth="sm"
            title="Reactivate user?"
            icon={CheckCircle}
          >
            <div>
              <p className="text-[14px] text-[#C5C5C5] leading-relaxed mb-6">
                This user will regain access to sign in.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReactivateUser}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#40E0D0] hover:brightness-105 text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50"
                >
                  {actionLoading ? 'Reactivating...' : 'Reactivate'}
                </button>
              </div>
            </div>
          </CivicModal>
        )}

        {/* Delete Confirmation Modal */}
        {selectedUser && confirmAction === 'delete' && (
          <CivicModal
            isOpen={true}
            onClose={() => setConfirmAction(null)}
            maxWidth="sm"
            title="Delete user?"
            icon={AlertTriangle}
          >
            <div>
              <p className="text-[14px] text-[#C5C5C5] leading-relaxed mb-6">
                This will remove the account from the active users list and revoke access immediately. This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#FF6F61] hover:brightness-105 text-white rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50"
                >
                  {actionLoading ? 'Deleting...' : 'Delete user'}
                </button>
              </div>
            </div>
          </CivicModal>
        )}

        {/* Reset Civic Profile Confirmation Modal */}
        {selectedUser && confirmAction === 'reset_civic' && (
          <CivicModal
            isOpen={true}
            onClose={() => setConfirmAction(null)}
            maxWidth="sm"
            title="Reset civic profile?"
            icon={RotateCcw}
          >
            <div>
              <p className="text-[14px] text-[#C5C5C5] leading-relaxed mb-6">
                This will reset the user's civic progress and badges. Account and analytics data will not be affected.
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmAction(null)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[AdminUsers] Reset button clicked');
                    handleResetCivicProfile();
                  }}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 bg-[#FF6F61] hover:brightness-105 text-white rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50"
                >
                  {actionLoading ? 'Resetting...' : 'Reset Profile'}
                </button>
              </div>
            </div>
          </CivicModal>
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </AdminShell>
  );
}
